import { useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { FilterableSelect } from '../common/FilterableSelect';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/Delete';
import type { ConflictDto } from '../../api/queries/conflicts';

const CONFLICT_TYPES = [
  'internal',
  'interpersonal',
  'societal',
  'environmental',
  'supernatural',
] as const;

const RESOLUTION_STATUSES = [
  { value: 'unresolved', label: 'Unresolved', color: '#f44336' },
  { value: 'partial', label: 'Partial', color: '#ff9800' },
  { value: 'resolved', label: 'Resolved', color: '#4caf50' },
  { value: 'escalated', label: 'Escalated', color: '#9c27b0' },
] as const;

interface ConflictSectionProps {
  conflicts: ConflictDto[];
  onCreate: (input: {
    title: string;
    conflictType?: string;
    description?: string;
    resolutionStatus?: string;
  }) => void;
  onDelete: (id: string) => void;
}

export function ConflictSection({
  conflicts,
  onCreate,
  onDelete,
}: ConflictSectionProps) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [type, setType] = useState('');
  const [desc, setDesc] = useState('');

  const handleCreate = () => {
    if (!title.trim()) return;
    onCreate({
      title: title.trim(),
      conflictType: type || undefined,
      description: desc || undefined,
      resolutionStatus: 'unresolved',
    });
    setTitle('');
    setType('');
    setDesc('');
    setOpen(false);
  };

  return (
    <Accordion defaultExpanded={conflicts.length > 0}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="subtitle2">
          Conflicts ({conflicts.length})
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={1}>
          {conflicts.map((c) => {
            const statusInfo = RESOLUTION_STATUSES.find(
              (s) => s.value === c.resolutionStatus,
            );
            return (
              <Box
                key={c.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  p: 1,
                  bgcolor: 'action.hover',
                  borderRadius: 1,
                }}
              >
                <Typography variant="body2" sx={{ flex: 1 }}>
                  {c.title}
                </Typography>
                {c.conflictType && (
                  <Chip
                    label={c.conflictType}
                    size="small"
                    variant="outlined"
                  />
                )}
                {statusInfo && (
                  <Chip
                    label={statusInfo.label}
                    size="small"
                    sx={{
                      bgcolor: statusInfo.color,
                      color: '#fff',
                    }}
                  />
                )}
                <IconButton
                  size="small"
                  onClick={() => onDelete(c.id)}
                  color="error"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            );
          })}

          <Button variant="outlined" size="small" onClick={() => setOpen(true)}>
            Add Conflict
          </Button>
        </Stack>

        <Dialog
          open={open}
          onClose={() => setOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Add Conflict</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                label="Title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                fullWidth
                required
              />
              <FilterableSelect
                options={CONFLICT_TYPES.map((t) => ({
                  value: t,
                  label: t.charAt(0).toUpperCase() + t.slice(1),
                }))}
                value={type}
                onChange={(newValue) => setType(newValue)}
                label="Type"
                allowEmpty={true}
                emptyLabel="None"
              />
              <TextField
                label="Description"
                value={desc}
                onChange={(e) => setDesc(e.target.value)}
                fullWidth
                multiline
                rows={3}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleCreate}
              disabled={!title.trim()}
            >
              Create
            </Button>
          </DialogActions>
        </Dialog>
      </AccordionDetails>
    </Accordion>
  );
}
